import { useState, useEffect } from 'react'
import { type MRT_PaginationState } from 'material-react-table'
import {
  getReviewReports,
  ignoreReviewReport,
  deleteReviewAndReport,
  type IReviewReportWithDetails,
} from '../../../api/reviews'
import { ReportReviewsTable } from './ReportReviewsTable'
import { ConfirmationDialog } from '../../../components/dialogs/ConfirmationDialog'

interface ReportViewProps {
  hideHeader?: boolean
}

export const ReportView = ({ hideHeader = false }: ReportViewProps) => {
  const [reports, setReports] = useState<IReviewReportWithDetails[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)

  // Dialog states
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<IReviewReportWithDetails | null>(null)

  useEffect(() => {
    loadReports()
  }, [pagination.pageIndex, pagination.pageSize])

  const loadReports = async () => {
    try {
      setIsLoading(true)
      const page = pagination.pageIndex + 1
      const result = await getReviewReports(page, pagination.pageSize)
      setReports(result.items)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error('Error loading review reports:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleView = (report: IReviewReportWithDetails) => {
    if (report.fragranceSlug) {
      window.open(`/fragrance/${report.fragranceSlug}?reviewId=${report.reviewId}`, '_blank')
    } else {
      alert('Unable to navigate: Fragrance not found for this review.')
    }
  }

  const handleIgnore = (report: IReviewReportWithDetails) => {
    setSelectedReport(report)
    setIgnoreDialogOpen(true)
  }

  const handleDelete = (report: IReviewReportWithDetails) => {
    setSelectedReport(report)
    setDeleteDialogOpen(true)
  }

  const confirmIgnore = async () => {
    if (!selectedReport) return

    try {
      await ignoreReviewReport(selectedReport.id)
      setIgnoreDialogOpen(false)
      setSelectedReport(null)
      loadReports()
    } catch (error) {
      console.error('Error ignoring report:', error)
      alert('Failed to ignore report. Please try again.')
    }
  }

  const confirmDelete = async () => {
    if (!selectedReport) return

    try {
      await deleteReviewAndReport(selectedReport.id, selectedReport.reviewId)
      setDeleteDialogOpen(false)
      setSelectedReport(null)
      loadReports()
    } catch (error) {
      console.error('Error deleting review:', error)
      alert('Failed to delete review. Please try again.')
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-8 pb-6 pt-8 border-searchBorder">
          <h1 className="text-2xl font-bold">Review Reports</h1>
        </div>
      )}

      {/* Material React Table */}
      <div className="flex-1 overflow-hidden">
        <ReportReviewsTable
          data={reports}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onView={handleView}
          onIgnore={handleIgnore}
          onDelete={handleDelete}
        />
      </div>

      {/* Ignore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={ignoreDialogOpen}
        onClose={() => setIgnoreDialogOpen(false)}
        onConfirm={confirmIgnore}
        title="Ignore Report"
        content={`Are you sure you want to ignore this report? The report will be dismissed but the review will remain visible.`}
        confirmLabel="Ignore Report"
        cancelLabel="Cancel"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Review"
        content={`Are you sure you want to delete this review? This action cannot be undone. The review by "${selectedReport?.reviewAuthorUsername}" will be permanently removed.`}
        confirmLabel="Delete Review"
        cancelLabel="Cancel"
      />
    </div>
  )
}
